<template>
  <div
    v-if="initialized && project_id"
    class="h-full w-full"
  >
    <main
      id="page"
      class="flex flex-col md:flex-row w-full h-full"
    >
      <TheNavbarTop v-if="mq.isMobile" />
      <Sidebar v-else />
      <router-view />
      <TheNotificationsSidebar v-show="show_notification_sidebar" />
    </main>
    <SurveysPopup
      v-if="surveys_popup_active"
      :project_id="project_id"
      :survey_id="active_survey_id"
      @close="closeSurveysPopup"
    />
    <ButtonImpersonateStop
      v-if="current_profile_true.type === PROFILE_TYPES.EMPLOYEE"
      :profile="current_profile"
    />
  </div>
</template>

<script>
import Sidebar from '@/app/_customers/components/navbar/TheSidebar.vue';
import SurveysPopup from '@/app/shared_components/surveys/SurveysPopup.vue';
import ButtonImpersonateStop from '@/app/shared_components/buttons/ButtonImpersonateStop.vue';
import { mapActions, mapGetters } from 'vuex';
import { Tenant } from '@/app/data/model_constants';
import { ROUTES_CUSTOMERS } from '@/app/data/route_constants';
import profile_mixin from '@/app/util/profile_mixin';
import TheNavbarTop from '@/app/_customers/components/navbar/TheNavbarTop.vue';
import TheNotificationsSidebar from '@/app/shared_components/navbar/notifications/TheNotificationsSidebar.vue';
import SIDEBAR_NOTIFICATIONS from '@/app/data/sidebar_constants';

export default {
  name: 'Customer',
  components: {
    Sidebar,
    SurveysPopup,
    ButtonImpersonateStop,
    TheNavbarTop,
    TheNotificationsSidebar,
  },
  mixins: [profile_mixin],

  data() {
    return {
      active_survey_id: null,
      surveys_popup_active: true,
      is_menu_open: false,
    };
  },

  computed: {
    ...mapGetters(['current_client', 'current_profile', 'sidebar_type']),

    show_notification_sidebar() {
      return this.sidebar_type === SIDEBAR_NOTIFICATIONS;
    },

    is_birthday() {
      if (this.current_client.attributes.status === Tenant.STATUS_TEST) return false;

      const birth_date = new Date(this.current_profile.attributes.birth_date);
      const today = new Date();
      return birth_date && birth_date.getMonth() === today.getMonth() && birth_date.getDate() === today.getDate();
    },
  },

  watch: {
    project_id: {
      handler() {
        this.surveys_popup_active = true;
      },
    },
    initialized: {
      immediate: true,
      handler(value) {
        if (value) this.initConfetti();
      },
    },
  },

  async created() {
    // survey popup handler
    this.$root.emitter.$on('initSurvey', (survey_id) => {
      this.active_survey_id = survey_id;
      this.surveys_popup_active = true;
    });

    await this.initProfile();
    await this.initProject();
  },

  methods: {
    ...mapActions(['FETCH_UNITS']),

    async fetchUnits() {
      await this.FETCH_UNITS();
    },

    closeSurveysPopup() {
      this.surveys_popup_active = false;
      this.active_question_id = null;
      this.initConfetti();
    },

    initProject() {
      if (_.isEmpty(this.projects)) {
        // customer does not have any (visible) units: trigger logout
        this.emitter.$emit('logout');
      } else {
        // project_id route param takes precedence, scoped with fallback to first unit's project
        let project_id = this.$route.params.project_id;
        if (project_id && !(project_id in this.projects)) {
          project_id = null;
        }
        if (!project_id) {
          const project = _.chain(this.projects).values(this.projects).maxBy('attributes.created_at').value();
          project_id = project.id;
          let route_name = this.$route.name;
          // convert "root" routes to project namespaced routes
          if (this.$route.name === ROUTES_CUSTOMERS.ROOT) {
            route_name = ROUTES_CUSTOMERS.DASHBOARD;
          } else if (this.$route.name === ROUTES_CUSTOMERS.ROOT_SETTINGS_PROFILE) {
            route_name = ROUTES_CUSTOMERS.SETTINGS_PROFILE;
          }
          this.$router.replace({ name: route_name, params: { project_id } });
        }
      }
    },

    initConfetti() {
      if (!this.is_birthday) return;
      if (this.surveys_popup_active) return; // no confetti with survey modal, invoke on closeSurveysPopup()

      const confetti_at = new Date().getFullYear().toString();
      const confettied_at = localStorage.getItem('confettied_at');
      if (confettied_at && confettied_at === confetti_at) return;
      this.$buefy.toast.open({
        message: this.$t('customer.toasts.birthday', { first_name: this.current_profile.attributes.first_name }),
        type: 'is-success',
        duration: 5000,
      });
      this.$confetti.start();
      setTimeout(() => this.$confetti.stop(), 5000);
      localStorage.setItem('confettied_at', confetti_at);
    },
  },
};
</script>
